
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';

export async function getPlatformAdmins(): Promise<User[]> {
  const { data, error } = await supabase
    .from('platform_admins')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching platform admins:', error);
    throw error;
  }

  return (data || []).map(admin => ({
    id: admin.id,
    name: admin.full_name || admin.email,
    email: admin.email,
    role: 'Admin' as const,
    status: admin.is_active ? 'Active' : 'Inactive',
    lastActive: admin.updated_at,
    invitationStatus: admin.invitation_status as 'pending' | 'accepted' | 'expired',
    invitedAt: admin.invited_at,
    invitedBy: admin.invited_by,
    user_type: 'platform_admin' as const,
    organization_id: undefined,
    organization_name: undefined
  }));
}

export async function createPlatformAdmin(adminData: {
  id: string;
  email: string;
  full_name?: string;
  invited_by?: string;
}) {
  const { data, error } = await supabase
    .from('platform_admins')
    .insert([{
      id: adminData.id,
      email: adminData.email,
      full_name: adminData.full_name,
      invited_by: adminData.invited_by,
      invitation_status: 'pending'
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating platform admin:', error);
    throw error;
  }

  return data;
}

export async function updatePlatformAdmin(id: string, updates: {
  full_name?: string;
  is_active?: boolean;
  invitation_status?: string;
}) {
  const { data, error } = await supabase
    .from('platform_admins')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating platform admin:', error);
    throw error;
  }

  return data;
}

export async function deletePlatformAdmin(id: string) {
  // First, set all invited_by references to NULL for users invited by this admin
  const { error: updateError } = await supabase
    .from('platform_admins')
    .update({ invited_by: null })
    .eq('invited_by', id);

  if (updateError) {
    console.error('Error updating invited_by references:', updateError);
    throw updateError;
  }

  // Also update organization_members table in case they invited org users
  const { error: updateOrgError } = await supabase
    .from('organization_members')
    .update({ invited_by: null })
    .eq('invited_by', id);

  if (updateOrgError) {
    console.error('Error updating organization_members invited_by references:', updateOrgError);
    throw updateOrgError;
  }

  // Now delete the platform admin
  const { error } = await supabase
    .from('platform_admins')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting platform admin:', error);
    throw error;
  }
}
